// src/components/layout/Layout.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navVisible, setNavVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrolled(currentScrollY > 10);
      if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
        setNavVisible(false);
        setIsMenuOpen(false);
      } else {
        setNavVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About Us' },
    { to: '/services', label: 'Services' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navVisible ? 'translate-y-0' : '-translate-y-full'} ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between h-16 items-center">
            {/* Logo and Brand */}
            <Link to="/" className="flex items-center gap-2 group">
              <img src="/favicon.ico" alt="Zia Dental Care Logo" className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105" />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-transparent">
                Zia Dental Care
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`relative px-4 py-2 text-sm font-medium rounded-md transition-all duration-300
                    ${isActive(link.to)
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <motion.div
                      layoutId="navbar-indicator"
                      className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-600 rounded-full"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                </Link>
              ))}
              <Link
                to="/appointment"
                className="ml-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 hover:shadow-lg hover:shadow-blue-200"
              >
                Book Appointment
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-blue-600 p-2 rounded-md hover:bg-blue-50 transition-colors"
                type="button"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
            >
              <div className="px-4 pt-2 pb-4 space-y-1">
                {navLinks.map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all 
                      ${isActive(link.to)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                      }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/appointment"
                  className="block w-full text-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg text-sm font-medium mt-2"
                >
                  Book Appointment
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Spacer for fixed nav */}
      <div className="h-12" />

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
          {/* Top section with logo */}
          <div className="flex items-center gap-3 mb-10">
            <img src="/favicon.ico" alt="Logo" className="h-12 w-12 object-contain" />
            <div>
              <h2 className="text-xl font-bold text-white">Zia Dental Care</h2>
              <p className="text-sm text-gray-400">Your Family Dentist</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact Us</h3>
              <div className="space-y-3">
                <a href="tel:+923147776565" className="flex items-center group hover:text-blue-400 transition-colors">
                  <Phone className="h-4 w-4 mr-3 text-blue-500" />
                  <span className="text-sm">+92 314 7776565</span>
                </a>
                <a href="mailto:clinicziadental@gmail.com" className="flex items-center group hover:text-blue-400 transition-colors">
                  <Mail className="h-4 w-4 mr-3 text-blue-500" />
                  <span className="text-sm">clinicziadental@gmail.com</span>
                </a>
                <a href='https://www.google.com/maps/place/Zia+Dental+Care/@34.0788388,72.6081485,986m/data=!3m2!1e3!4b1!4m6!3m5!1s0x38defbb4cff22761:0x9fa2b4873b31bea7!8m2!3d34.0788388!4d72.6107234!16s%2Fg%2F11fsx7c8mz?entry=ttu&g_ep=EgoyMDI0MTIxMS4wIKXMDSoASAFQAw%3D%3D' className="flex items-start group hover:text-blue-400 transition-colors">
                  <MapPin className="h-4 w-4 mr-3 mt-0.5 text-blue-500 flex-shrink-0" />
                  <span className="text-sm">Topi, Swabi, KPK, Pakistan</span>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {['About Us', 'Our Services', 'Contact Us', 'Book Appointment'].map((label, i) => (
                  <li key={i}>
                    <Link
                      to={['about', 'services', 'contact', 'appointment'][i] ? `/${['about', 'services', 'contact', 'appointment'][i]}` : '/'}
                      className="text-sm hover:text-blue-400 transition-colors hover:translate-x-1 inline-block"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Hours */}
            <div>
              <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Operating Hours</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between"><span>Mon – Thu</span><span className="text-white font-medium">10:30 AM – 8:00 PM</span></li>
                <li className="flex justify-between"><span>Sat – Sun</span><span className="text-white font-medium">10:30 AM – 8:00 PM</span></li>
                <li className="flex justify-between"><span>Friday</span><span className="text-red-400 font-medium">Closed</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800 text-center">
            <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Zia Dental Care. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;